import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import NotificationBell from '../admin/NotificationBell';
import { useCompanyLogo } from '@/hooks/useCompanyLogo';
import LoadingOverlay from '../base/LoadingOverlay';

const HEADER_HEIGHT = 73;

// ── helpers ──────────────────────────────────────────────

function isRouteActive(currentPath: string, itemPath: string, matchSubRoutes: boolean = false): boolean {
  if (currentPath === itemPath) return true;
  if (matchSubRoutes && currentPath.startsWith(itemPath + '/')) return true;
  return false;
}

// ── section header ───────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="pt-4 pb-2 first:pt-0">
      <div className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
        {label}
      </div>
    </div>
  );
}

// ── nav link ─────────────────────────────────────────────

function NavLink({
  to,
  icon,
  label,
  isActive,
}: {
  to: string;
  icon: string;
  label: string;
  isActive: boolean;
  matchSubRoutes?: boolean;
}) {
  return (
    <Link
      to={to}
      data-nav-active={isActive ? 'true' : 'false'}
      className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
        isActive
          ? 'bg-teal-50 text-teal-700'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      <i className={`${icon} text-lg mr-3`}></i>
      {label}
    </Link>
  );
}

// ── layout ───────────────────────────────────────────────

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyLogo = useCompanyLogo();
  const [isNavigating, setIsNavigating] = useState(false);
  const prevPathRef = useRef(location.pathname);

  // Refs para scrolls independientes
  const sidebarScrollRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Loading overlay en transiciones
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      setIsNavigating(true);
      prevPathRef.current = location.pathname;
      const timer = setTimeout(() => setIsNavigating(false), 800);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // ── Scroll del main content hacia arriba al cambiar de ruta ──
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  // ── Mantener visible el elemento activo del sidebar (solo si no está visible) ──
  useEffect(() => {
    const container = sidebarScrollRef.current;
    if (!container) return;

    const activeLink = container.querySelector('[data-nav-active="true"]') as HTMLElement | null;
    if (!activeLink) return;

    // Solo hacer scroll si el elemento no está completamente visible
    const containerRect = container.getBoundingClientRect();
    const activeRect = activeLink.getBoundingClientRect();
    const isFullyVisible =
      activeRect.top >= containerRect.top &&
      activeRect.bottom <= containerRect.bottom;

    if (!isFullyVisible) {
      requestAnimationFrame(() => {
        activeLink.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  // ── helpers para active state ──
  const currentPath = location.pathname;

  const active = useCallback(
    (path: string, matchSubRoutes: boolean = false) => isRouteActive(currentPath, path, matchSubRoutes),
    [currentPath],
  );

  return (
    <>
      {isNavigating && <LoadingOverlay message="Cargando..." />}

      {/* Shell: ocupa toda la pantalla, nada de scroll en el documento */}
      <div className="h-screen overflow-hidden flex flex-col bg-gray-50">

        {/* ── Header fijo ── */}
        <header
          className="flex-shrink-0 bg-white border-b border-gray-200 z-20"
          style={{ height: HEADER_HEIGHT + 'px' }}
        >
          <div className="h-full px-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={companyLogo || 'https://fotosyvideosantarosa.com/storage/2025/12/fyvnueva2.png'}
                alt="Fotos y Video Santa Rosa Logo"
                className="h-10 w-auto object-contain"
              />
              <h1 className="text-xl font-bold text-gray-900">
                Panel Admin Fotos y Video Santa Rosa
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBell />
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </header>

        {/* ── Cuerpo: sidebar + contenido ── */}
        <div className="flex-1 min-h-0 overflow-hidden flex">

          {/* ── Sidebar con scroll propio ── */}
          <aside
            ref={sidebarScrollRef}
            className="w-64 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto overflow-x-hidden"
          >
            <nav className="px-4 py-6 space-y-1 pb-10">
              {/* Dashboard */}
              <NavLink to="/admin/dashboard" icon="ri-dashboard-line" label="Dashboard" isActive={active('/admin/dashboard')} />

              {/* Ventas */}
              <SectionHeader label="Ventas" />
              <NavLink to="/admin/leads" icon="ri-user-add-line" label="Leads" isActive={active('/admin/leads')} />
              <NavLink to="/admin/clients" icon="ri-team-line" label="Clientes" isActive={active('/admin/clients')} />
              <NavLink to="/admin/client-tokens" icon="ri-key-line" label="Tokens de Acceso" isActive={active('/admin/client-tokens')} />

              {/* Agenda */}
              <SectionHeader label="Agenda" />
              <NavLink to="/admin/appointments" icon="ri-calendar-check-line" label="Citas" isActive={active('/admin/appointments')} />
              <NavLink to="/admin/calendar" icon="ri-calendar-line" label="Calendario" isActive={active('/admin/calendar')} />
              <NavLink to="/admin/sessions" icon="ri-camera-line" label="Sesiones Pre-evento" isActive={active('/admin/sessions')} />
              <NavLink to="/admin/event-attention" icon="ri-map-pin-5-line" label="Atención de Evento" isActive={active('/admin/event-attention')} />

              {/* Contratos y Productos */}
              <SectionHeader label="Contratos y Productos" />
              <NavLink to="/admin/contracts" icon="ri-file-text-line" label="Contratos" isActive={active('/admin/contracts')} />
              <NavLink to="/admin/contract-templates" icon="ri-file-copy-line" label="Plantillas de Contratos" isActive={active('/admin/contract-templates')} />
              <NavLink to="/admin/packages" icon="ri-box-3-line" label="Paquetes" isActive={active('/admin/packages')} />
              <NavLink to="/admin/precios" icon="ri-price-tag-3-line" label="Cotizador Online" isActive={active('/admin/precios')} />
              <NavLink to="/admin/service-config" icon="ri-settings-4-line" label="Add-ons / Servicios Premium" isActive={active('/admin/service-config')} />
              <NavLink to="/admin/individual-products" icon="ri-shopping-bag-3-line" label="Productos Individuales" isActive={active('/admin/individual-products')} />

              {/* Operaciones */}
              <SectionHeader label="Operaciones" />
              <NavLink to="/admin/jobs" icon="ri-briefcase-line" label="Trabajos" isActive={active('/admin/jobs', true)} />
              <NavLink to="/admin/entregas" icon="ri-download-cloud-line" label="Entregas" isActive={active('/admin/entregas')} />

              {/* Marketing */}
              <SectionHeader label="Marketing" />
              <NavLink to="/admin/email-campaigns" icon="ri-mail-send-line" label="Enviar Correo" isActive={active('/admin/email-campaigns')} />
              <NavLink to="/admin/ruleta-premios" icon="ri-gift-line" label="Ruleta de Premios" isActive={active('/admin/ruleta-premios')} />

              {/* Contenido */}
              <SectionHeader label="Contenido" />
              <NavLink to="/admin/site-content" icon="ri-pages-line" label="Contenido del Sitio" isActive={active('/admin/site-content')} />

              {/* Herramientas Creativas */}
              <SectionHeader label="Herramientas Creativas" />
              <NavLink to="/admin/dvd-builder" icon="ri-disc-line" label="DVD Builder" isActive={active('/admin/dvd-builder')} />
              <NavLink to="/admin/dvds" icon="ri-folder-music-line" label="DVDs Publicados" isActive={active('/admin/dvds')} />
              <NavLink to="/admin/tv-apps" icon="ri-smartphone-line" label="Apps de TV" isActive={active('/admin/tv-apps')} />
              <NavLink to="/admin/pose-guides" icon="ri-book-open-line" label="Guías de Poses" isActive={active('/admin/pose-guides')} />
              <NavLink to="/admin/invitations" icon="ri-coupon-line" label="Invitations" isActive={active('/admin/invitations')} />
              <NavLink to="/admin/photobooks" icon="ri-book-2-line" label="Photobooks" isActive={active('/admin/photobooks')} />
              <NavLink to="/admin/photobook-frames" icon="ri-layout-masonry-line" label="Frames de Photobook" isActive={active('/admin/photobook-frames')} />

              {/* Muestras */}
              <SectionHeader label="Muestras" />
              <NavLink to="/admin/samples/photos" icon="ri-image-line" label="Fotos" isActive={active('/admin/samples/photos')} />
              <NavLink to="/admin/samples/video" icon="ri-video-line" label="Video" isActive={active('/admin/samples/video')} />
              <NavLink to="/admin/help-videos" icon="ri-question-answer-line" label="Videos de Ayuda" isActive={active('/admin/help-videos')} />

              {/* Impresión */}
              <SectionHeader label="Impresión" />
              <NavLink to="/admin/print-templates" icon="ri-printer-line" label="Plantillas de Impresión" isActive={active('/admin/print-templates')} />
              <NavLink to="/admin/print-approvals" icon="ri-check-double-line" label="Print Approvals" isActive={active('/admin/print-approvals', true)} />

              {/* AI Production */}
              <SectionHeader label="AI Production" />
              <NavLink to="/admin/studioflow" icon="ri-robot-2-line" label="StudioFlow Dashboard" isActive={active('/admin/studioflow')} />
              <NavLink to="/admin/studioflow/process-photos" icon="ri-camera-line" label="Process Photos" isActive={active('/admin/studioflow/process-photos')} />
              <NavLink to="/admin/studioflow/process-video" icon="ri-video-line" label="Process Video" isActive={active('/admin/studioflow/process-video')} />
              <NavLink to="/admin/studioflow/review-results" icon="ri-check-double-line" label="Review Results" isActive={active('/admin/studioflow/review-results')} />
              <NavLink to="/admin/studioflow/production-history" icon="ri-history-line" label="Production History" isActive={active('/admin/studioflow/production-history')} />
              <NavLink to="/admin/studioflow/settings" icon="ri-settings-5-line" label="StudioFlow Settings" isActive={active('/admin/studioflow/settings')} />

              {/* Configuración */}
              <SectionHeader label="Configuración" />
              <NavLink to="/admin/settings/smtp2go" icon="ri-mail-settings-line" label="SMTP2GO" isActive={active('/admin/settings/smtp2go')} />
              <NavLink to="/admin/settings/tracking-pixels" icon="ri-code-s-slash-line" label="Píxeles de Seguimiento" isActive={active('/admin/settings/tracking-pixels')} />
            </nav>
          </aside>

          {/* ── Contenido principal con scroll propio ── */}
          <main
            ref={mainContentRef}
            className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden"
          >
            <Outlet />
          </main>

        </div>
      </div>
    </>
  );
}